import * as THREE from 'three';
import type { VirtualFS } from '../fs/vfs';
import { decodeTga } from './tga';

/** Loads and caches textures out of the virtual filesystem. */
export class TextureManager {
  private cache = new Map<string, Promise<THREE.Texture | null>>();
  readonly missing: Set<string> = new Set();
  readonly fallback: THREE.Texture;
  readonly white: THREE.Texture;

  constructor(
    private readonly vfs: VirtualFS,
    private readonly anisotropy: number,
  ) {
    this.fallback = makeChecker();
    this.white = makeSolid(255, 255, 255);
  }

  /** Loads "textures/foo/bar" trying .tga then .jpg (and the exact name). */
  load(name: string, clamp = false): Promise<THREE.Texture | null> {
    const base = name.toLowerCase().replace(/\.(tga|jpg|jpeg|png)$/, '');
    const key = `${base}|${clamp ? 1 : 0}`;
    let p = this.cache.get(key);
    if (!p) {
      p = this.loadUncached(base, name, clamp);
      this.cache.set(key, p);
    }
    return p;
  }

  /** Is there an image file for this name (synchronous; "$..." names are built in)? */
  exists(name: string): boolean {
    if (name.startsWith('$')) return true;
    const base = name.toLowerCase().replace(/\.(tga|jpg|jpeg|png)$/, '');
    return [`${base}.tga`, `${base}.jpg`, `${base}.png`].some((p) => this.vfs.has(p));
  }

  private async loadUncached(base: string, original: string, clamp: boolean): Promise<THREE.Texture | null> {
    const candidates = [`${base}.tga`, `${base}.jpg`, `${base}.png`, original];
    for (const path of candidates) {
      if (!this.vfs.has(path)) continue;
      try {
        const data = await this.vfs.read(path);
        if (!data) continue;
        const tex = await decodeTexture(path, data);
        this.configure(tex, clamp);
        return tex;
      } catch (err) {
        console.warn(`texture ${path}:`, err);
      }
    }
    this.missing.add(base);
    return null;
  }

  configure(tex: THREE.Texture, clamp: boolean): void {
    tex.flipY = false; // Quake texture coordinates have t=0 at the top row
    tex.wrapS = tex.wrapT = clamp ? THREE.ClampToEdgeWrapping : THREE.RepeatWrapping;
    tex.magFilter = THREE.LinearFilter;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.generateMipmaps = true;
    tex.anisotropy = this.anisotropy;
    tex.colorSpace = THREE.NoColorSpace;
    tex.needsUpdate = true;
  }

  dispose(): void {
    for (const p of this.cache.values()) p.then((t) => t?.dispose());
    this.cache.clear();
  }
}

async function decodeTexture(path: string, data: Uint8Array): Promise<THREE.Texture> {
  if (path.endsWith('.tga')) {
    const img = decodeTga(data);
    return new THREE.DataTexture(img.data, img.width, img.height, THREE.RGBAFormat);
  }
  const bitmap = await createImageBitmap(new Blob([data as BlobPart]), {
    imageOrientation: 'none',
    premultiplyAlpha: 'none',
    colorSpaceConversion: 'none',
  });
  return new THREE.Texture(bitmap as unknown as HTMLImageElement);
}

function makeChecker(): THREE.Texture {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const grid = x % 32 === 0 || y % 32 === 0;
      const v = grid ? 90 : ((x >> 4) ^ (y >> 4)) & 1 ? 150 : 170;
      const i = (y * size + x) * 4;
      data[i] = v;
      data[i + 1] = v;
      data[i + 2] = v + 10;
      data[i + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

export function makeSolid(r: number, g: number, b: number): THREE.DataTexture {
  const tex = new THREE.DataTexture(new Uint8Array([r, g, b, 255]), 1, 1, THREE.RGBAFormat);
  tex.needsUpdate = true;
  return tex;
}

export { colorShift } from './colorShift';
